<template>
  <vs-popup id="discount-popup" class="holamundo" :title="popupTitle" :active.sync="tmpActive">
    <div class="vx-col w-full">
      <div class="vx-row px-5 mb-5">
        <div class="vx-col w-full">
          <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label class="text-sm">Weight Type</label>
              <select-2
                autocomplete
                class="w-full"
                :value="discount.weight_type"
                @input="
                  (item) => {
                    discount.weight_type = item && item.value
                  }
                "
                name="Weight Type"
                v-validate="'required'"
                placeholder="Select Weight Type"
                :options="[categories.FLOWER, categories.SHAKE].includes(form.categoryId) ? weightTypes : [{ label: 'Unit', value: 'unit' }]"
              />
              <span class="text-danger text-sm" v-show="errors.has('Weight Type')">{{ errors.first('Weight Type') }}</span>
            </div>
            <div>
              <label class="text-sm">Weight/Package</label>
              <vs-input
                type="number"
                step="0.001"
                :disabled="![categories.FLOWER, categories.SHAKE].includes(form.categoryId)"
                v-model="discount.weight_per_package"
                @change="(e) => (discount.weight_per_package = mathRound(e.target.value, 3))"
                icon="icon icon-list"
                icon-pack="feather"
                class="w-full"
                name="Weight/Package"
                v-validate="[categories.FLOWER, categories.SHAKE].includes(form.categoryId) ? 'required' : ''"
              />
              <span class="text-danger text-sm" v-show="errors.has('Weight/Package')">{{ errors.first('Weight/Package') }}</span>
            </div>
            <div>
              <vs-input
                type="number"
                step="0.001"
                :disabled="[categories.FLOWER, categories.SHAKE].includes(form.categoryId)"
                v-model="discount.quantity"
                @change="(e) => (discount.quantity = mathRound(e.target.value, 3))"
                icon="icon icon-list"
                icon-pack="feather"
                class="w-full"
                label="Quantity"
                name="Quantity"
                v-validate="![categories.FLOWER, categories.SHAKE].includes(form.categoryId) ? 'required' : ''"
              />
              <span class="text-danger text-sm" v-show="errors.has('Quantity')">{{ errors.first('Quantity') }}</span>
            </div>
            <div>
              <vs-input
                type="number"
                step="0.01"
                v-model="discount.sales_price"
                @change="(e) => (discount.sales_price = mathRound(e.target.value, 2))"
                icon="icon icon-list"
                icon-pack="feather"
                class="w-full"
                label="Sale Price"
                name="Sale Price"
                v-validate="'required'"
              />
              <span class="text-danger text-sm" v-show="errors.has('Sale Price')">{{ errors.first('Sale Price') }}</span>
            </div>
            <div class="relative">
              <label class="text-sm">Start Date</label>
              <date-picker
                :appendToBody="false"
                v-model="discount.start_date"
                name="Start Date"
                placeholder="MM-DD-YYYY"
                v-validate="'required'"
                format="MM-DD-YYYY"
                valueType="format"
                class="w-full"
                label-placeholder="Start Date"
                label="Start Date"
              />
              <span class="text-danger text-sm" v-show="errors.has('Start Date')">{{ errors.first('Start Date') }}</span>
            </div>
            <div class="relative">
              <label class="text-sm">End Date</label>
              <date-picker
                :appendToBody="false"
                v-model="discount.end_date"
                name="End Date"
                placeholder="MM-DD-YYYY"
                v-validate="'required'"
                format="MM-DD-YYYY"
                valueType="format"
                class="w-full"
                label-placeholder="End Date"
                label="End Date"
              />
              <span class="text-danger text-sm" v-show="errors.has('End Date')">{{ errors.first('End Date') }}</span>
            </div>
            <div>
              <label class="text-sm">Start Time</label>
              <flat-pickr v-model="discount.start_time" class="w-full" :config="config" />
            </div>
            <div>
              <label class="text-sm">End Time</label>
              <flat-pickr v-model="discount.end_time" class="w-full" :config="config" />
            </div>
          </div>
        </div>
      </div>
      <hr />
      <div class="vx-row justify-center mt-5">
        <vs-button class="mr-5" @click="addDiscount()" color="primary" type="relief">Save</vs-button>
        <vs-button @click="tmpActive = false" color="danger" type="relief">Close</vs-button>
      </div>
    </div>
  </vs-popup>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'

import { weightTypes, weightMixins } from '@/utils/weightType'

import Select2 from '@/components/custom/form-elements/Select2.vue'

export default {
  name: 'PromotionalDiscountPriceModal',
  components: {
    Select2,
    DatePicker
  },
  props: {
    active: {
      type: Boolean,
      default: false
    },
    discount: {
      type: Object
    },
    popupTitle: {
      type: String
    },
    form: {
      type: Object
    },
    showAll: {
      type: Boolean,
      default: true
    }
  },
  mixins: [weightMixins],
  data() {
    return {
      config: {
        enableTime: true,
        noCalendar: true,
        allowInput: true,
        time_24hr: false,
        formatDate: (d) => {
          return moment(d).format('HH:mm A')
        }
      },
      date: 'MM-dd-yyyy',
      disabledDates: {
        to: new Date(Date.now() - 8640000)
      },
      weightTypes
    }
  },
  computed: {
    ...mapState('product', ['categories']),

    tmpActive: {
      get() {
        return this.active
      },
      set(val) {
        this.$emit('update:active', val)
      }
    }
  },
  methods: {
    async addDiscount() {
      if (!(await this.$validator.validate())) {
        this.$vs.notify({
          title: 'Error',
          text: 'Please Fill The Form First',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'danger'
        })
        return
      }
      this.$emit('added', {
        ...this.discount,
        start_date: moment(this.discount.start_date).format('MM-DD-YYYY'),
        end_date: moment(this.discount.end_date).format('MM-DD-YYYY'),
        start_time: this.discount.start_time ? moment(this.discount.start_time, 'HH:mm A').format('HH:mm:ss') : '00:00:00',
        end_time: this.discount.end_time ? moment(this.discount.end_time, 'HH:mm A').format('HH:mm:ss') : '23:59:59'
      })
      this.tmpActive = false
    }
  },

  beforeRouteLeave(_, __, next) {
    this.tmpActive = false
    next()
  }
}
</script>

<style lang="scss">
#discount-popup {
  .vs-popup {
    width: calc(100% - 30%) !important;
    .vdp-datepicker {
      position: unset;
    }
  }
}
</style>
